import { isNotNil } from 'utils/predicate'
import { isNil, zipObject, zipWith } from 'lodash'
import { CURVE_POSTFIX_GROUP_COLUMN_HEADER } from 'view/routes/requests/incoming/curves/CurveNegotiationsGridModel'

interface Term {
  term: string
  startIdx: number
  endIdx: number
  headers: string[]
}

type Value = string | number | null

const getLastTermEndIdx = (groupRow: string[]) => {
  const index = groupRow.findIndex(v => v === CURVE_POSTFIX_GROUP_COLUMN_HEADER)
  if (index === -1) {
    return groupRow.length - 1
  }
  return index - 1
}

const getDefaultTermColumns = (terms: Term[]) =>
  zipObject([
    ...new Set(
      terms.reduce((acc, t) => {
        return [...acc, ...t.headers]
      }, [])
    )
  ])

const getCommonColumns = (row: Value[], headers: string[], terms: Term[], lastTermEndIdx: number) => {
  const [firstTerm] = terms
  const prefixColumsEndIdx = firstTerm.startIdx - 1
  return {
    ...zipObject(headers.slice(lastTermEndIdx + 1), row.slice(lastTermEndIdx + 1)),
    ...zipObject(headers.slice(0, prefixColumsEndIdx + 1), row.slice(0, prefixColumsEndIdx + 1))
  }
}

const buildTerms = (row: Value[], headers: string[], lastTermIndex: number) => {
  const isTerm = (cell: Value) => isNotNil(cell) && cell !== CURVE_POSTFIX_GROUP_COLUMN_HEADER
  const terms = row.filter(isTerm)
  const startIdxs = row.reduce((acc, term, index) => (isTerm(term) ? [...acc, index] : acc), [])
  const [, ...endIdxs] = [...startIdxs.map(idx => (idx -= 1)), lastTermIndex]

  return zipWith<Value, number, number, Term>(terms, startIdxs, endIdxs, (term, startIdx, endIdx) => ({
    term: String(term),
    startIdx,
    endIdx,
    headers: headers.slice(startIdx, endIdx + 1)
  }))
}

export type RowData = [string[], string[], ...Value[][]]

export const transformCurveImportData = (data: RowData) => {
  const [groupRow, curveHeaders, ...rows] = data

  const lastTermEndIndex = getLastTermEndIdx(groupRow)
  const terms = buildTerms(groupRow, curveHeaders, lastTermEndIndex)
  const defaultTermColumns = getDefaultTermColumns(terms)

  return rows.flatMap(row => {
    const commonColumns = getCommonColumns(row, curveHeaders, terms, lastTermEndIndex)
    return terms.reduce((acc, { term, startIdx, endIdx, headers }) => {
      const termRow = row.slice(startIdx, endIdx + 1)
      return termRow.every(t => isNil(t))
        ? acc
        : [...acc, { ...commonColumns, ...{ Term: term, ...defaultTermColumns, ...zipObject(headers, termRow) } }]
    }, [])
  })
}
